/*
 * Figma `122:8` — TODAY-01 · Command Center · Readiness + Decisions.
 *
 * `DashboardTemplate`: the metric strip, then what needs a person beside how ready the event is,
 * then the sales detail the registry promises under it.
 *
 * The frame's left column is a list of decisions with a severity, an owner and a consequence. Two
 * of the three are in the data and one is not. Severity is real — a task carries the priority its
 * organizer set, and a risk carries the score the register computed, so `priorityActions` reads
 * those rather than ranking things itself. Owner is real where a record names one and is left off
 * where none does. The consequence is where the frame writes deadlines: "ไม่ยืนยันภายใน 18:00
 * จะระงับการขาย". Nothing in this product suspends sales at a time, so that line would be a threat
 * the system does not carry out. What is said instead is what was counted.
 *
 * The right column is `computeReadiness`, unchanged: four phases, each a percentage and a state in
 * words, because a phase is ready when its blocking checks pass and not when its percentage looks
 * high enough. It loads on its own — twelve reads must not hold up the one that answers who is
 * waiting.
 *
 * TICK-148 — the decisions column starts with the blocking readiness checks that failed, because a
 * list that says "nothing pending" beside a card that says nothing can be sold contradicts itself.
 * Queue items still arrive first and alone; the setup items join when the twelve reads land, and
 * until then an otherwise-empty list says it is still checking rather than claiming it is clear.
 *
 * The frame's fourth tile is "อีก 12 วัน · 7 milestones", and both halves are now real.
 * `events.ends_at` reaches the console on the ops settings record, and the count is taken in the
 * event's own timezone, because a show ending just after midnight is on the next day in Bangkok and
 * the previous one in UTC. Milestones are tasks somebody marked (TICK-143), so they come from the
 * task list this screen already reads — no extra request for a count.
 */
import {
  Alert, BarChart, Button, Card, DashboardTemplate, FeedbackState, KpiCard, MeterRow, StatusBadge,
} from '../..';
import type { BadgeTone } from '../..';
import type { Destination } from '../../destinations';
import { fmt } from '../../../api';
import type { AdminStatsDto, RevenueSeriesRange, SalesChannelReportDto } from '../../../api';
import { fullyReadable } from '../../../readiness';
import type { PhaseReadiness, ReadinessTab } from '../../../readiness';
import type {
  ActionSeverity, CommandCenterState, EventReadinessState, RevenueTrendState,
} from './useCommandCenter';
import {
  priorityActions, setupActions, SLIP_ATTENTION_MINUTES, useCommandCenter, useEventReadiness,
  useRevenueTrend,
} from './useCommandCenter';
import { blockersOf } from './useOpsTasks';
import { countdownTo, countdownWords } from './eventDate';

const SEVERITY_LABEL: Record<ActionSeverity, string> = {
  critical: 'CRITICAL', high: 'HIGH', medium: 'MEDIUM', info: 'INFO',
};
const SEVERITY_TONE: Record<ActionSeverity, BadgeTone> = {
  critical: 'danger', high: 'warning', medium: 'accent', info: 'neutral',
};

const PHASE_STATE: Record<PhaseReadiness['state'], { text: string; tone: BadgeTone }> = {
  ready: { text: 'พร้อม', tone: 'success' },
  not_ready: { text: 'ยังไม่พร้อม', tone: 'danger' },
  partial_view: { text: 'ดูได้ไม่ครบ', tone: 'warning' },
  no_access: { text: 'ไม่มีสิทธิ์ดู', tone: 'neutral' },
};

const RANGES: { key: RevenueSeriesRange; label: string }[] = [
  { key: '7d', label: '7 วัน' },
  { key: '14d', label: '14 วัน' },
  { key: '30d', label: '1 เดือน' },
  { key: 'all', label: 'ทั้งหมด' },
];

const pct = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0);

export function CommandCenterScreen({
  destination, state, readiness, trend, stats, statsError, channels, permitted, role, onGoTo,
}: {
  destination: Destination;
  state: CommandCenterState;
  readiness: EventReadinessState;
  trend: RevenueTrendState;
  /** Polled by the shell. Taken as a prop so this screen cannot show a different number a second apart. */
  stats: AdminStatsDto | null;
  statsError?: string;
  channels: SalesChannelReportDto[] | null;
  permitted: boolean;
  role?: string | null;
  onGoTo: (tab: ReadinessTab | 'review', sub?: 'awaiting_verification' | 'problem') => void;
}) {
  const { awaiting, tasks, risks, withOps, loading, loadError, reload } = state;

  const queueActions = priorityActions({ awaiting, tasks, risks, stats });
  // TICK-148 — blocking setup checks that failed come first: they are the reason nothing can be sold.
  const actions = [...setupActions(readiness.readiness, role), ...queueActions];
  const lateSlips = queueActions.find((a) => a.id === 'slips_waiting');
  const blocked = queueActions.find((a) => a.id === 'tasks_blocked');
  // "Nothing pending" is only a fact when every blocking check could be read; otherwise say what was not.
  const readinessSettled = !readiness.loading && !readiness.error;
  const readinessComplete = readinessSettled && fullyReadable(readiness.readiness);
  const countdown = countdownTo(readiness.eventEndsAt, readiness.eventTimezone);
  // Milestones ride on the tasks already fetched; the count costs nothing extra.
  const milestones = tasks.filter((t) => t.isMilestone);
  const milestonesLeft = milestones.filter((t) => t.status !== 'done').length;
  const blockers = blockersOf(tasks);
  const blockedCount = tasks.filter((t) => t.status !== 'done' && (blockers.get(t.id)?.length ?? 0) > 0).length;

  const totalSold = stats ? stats.zones.reduce((s, z) => s + z.sold, 0) : 0;
  const totalCapacity = stats ? stats.zones.reduce((s, z) => s + z.capacity, 0) : 0;

  const phases = readiness.readiness?.phases ?? [];
  const namedChannels = (channels ?? []).filter((c) => c.channelId !== null);
  const topChannels = [...namedChannels].sort((a, b) => (b.clicks ?? 0) - (a.clicks ?? 0)).slice(0, 4);
  const maxClicks = Math.max(1, ...namedChannels.map((c) => c.clicks ?? 0));

  return (
    <DashboardTemplate
      gate={{ destination, permitted, role, loading, error: loadError || null, onRetry: reload }}
      kpis={
        <>
          <KpiCard
            label="ความพร้อมของงาน"
            value={readiness.loading ? '—' : readiness.readiness?.percent === null || !readiness.readiness
              ? 'ยังบอกไม่ได้'
              : `${readiness.readiness.percent}%`}
            tone={readiness.readiness?.partial ? 'warning' : 'neutral'}
            meta={
              readiness.error ? readiness.error
                : readiness.loading ? 'กำลังอ่าน…'
                  : readiness.readiness
                    ? `พร้อม ${readiness.readiness.readyPhases}/${readiness.readiness.visiblePhases} เฟส${readiness.readiness.partial ? ' · เท่าที่ Token นี้เห็น' : ''}`
                    : ''
            }
          />
          <KpiCard
            label="ตั๋วขายแล้ว"
            value={stats ? `${totalSold.toLocaleString('th-TH')} ใบ` : '—'}
            meta={
              statsError ? statsError
                : !stats ? 'กำลังโหลด…'
                  : totalCapacity > 0
                    ? `${pct(totalSold, totalCapacity).toFixed(1)}% ของความจุ ${totalCapacity.toLocaleString('th-TH')} ใบ`
                    : 'ยังไม่มีบัตรที่ตั้งความจุไว้'
            }
          />
          <KpiCard
            label="งานที่ติดอยู่"
            value={!withOps ? 'ไม่มีสิทธิ์ดู' : `${blockedCount.toLocaleString('th-TH')} งาน`}
            tone={blocked ? (blocked.severity === 'critical' ? 'danger' : 'warning') : 'neutral'}
            meta={
              !withOps ? 'งานและความเสี่ยงเป็นข้อมูลของทีมปฏิบัติการ'
                : blocked ? 'รออยู่เพราะงานก่อนหน้ายังไม่เสร็จ'
                  : 'ไม่มีงานที่รองานอื่นอยู่'
            }
          />
          <KpiCard
            label="สลิปที่รออยู่"
            value={`${awaiting.length.toLocaleString('th-TH')} รายการ`}
            tone={lateSlips ? 'danger' : 'neutral'}
            meta={
              lateSlips
                ? lateSlips.consequence
                : `ยังไม่มีรายการไหนรอเกิน ${SLIP_ATTENTION_MINUTES} นาที`
            }
          />
          {/* The drawn strip is four wide; the grid reflows rather than clipping a fifth. */}
          <KpiCard
            label="วันแสดง"
            value={countdownWords(countdown)}
            tone={countdown && countdown.days >= 0 && countdown.days <= 3 ? 'warning' : 'neutral'}
            meta={
              countdown
                ? `จบงาน ${countdown.day} · ${milestones.length > 0
                  ? `milestone เหลือ ${milestonesLeft} จาก ${milestones.length}`
                  : 'ยังไม่มี milestone'}`
                : readiness.loading ? 'กำลังอ่าน…'
                  : 'ยังไม่ได้ตั้งวันจบงานของงานนี้ — ทีมงาน JamTick เป็นคนตั้งให้'
            }
          />
        </>
      }
      actions={
        <Card
          title="ต้องจัดการวันนี้"
          sub="ข้อตั้งค่าที่ยังขาดมาก่อน แล้วเรียงจากสิ่งที่มีคนรออยู่ปลายทาง ไปหาสิ่งที่ทีมงานเป็นคนรอ"
        >
          {actions.length === 0 && readiness.loading ? (
            /* Not the empty state: the setup checks have not been read yet, so "nothing" is not known. */
            <FeedbackState state="empty" title="กำลังตรวจความพร้อมของงาน…">
              ยังไม่สรุปว่ามีอะไรค้างหรือไม่ จนกว่าจะอ่านข้อตั้งค่าที่ต้องมีครบ
            </FeedbackState>
          ) : actions.length === 0 ? (
            <FeedbackState state="empty" title="ไม่มีอะไรค้างอยู่ในตอนนี้">
              คิวสลิปว่าง งานไม่มีที่เลยกำหนดหรือติดอยู่ และไม่มีความเสี่ยงที่เปิดค้างไว้
              {readinessComplete && ' · ข้อตั้งค่าที่ต้องมีผ่านครบทุกเฟส'}
              {!readinessComplete && role !== 'sales' && (
                <>
                  <br />
                  {readiness.error
                    ? 'อ่านรายการความพร้อมไม่สำเร็จ จึงยังไม่นับข้อตั้งค่าในรายการนี้'
                    : 'ข้อตั้งค่าบางข้อที่ Token นี้อ่านไม่ได้ ไม่ได้นับรวมในรายการนี้'}
                </>
              )}
            </FeedbackState>
          ) : (
            <ul className="jt-actions">
              {actions.map((action) => (
                <li key={action.id}>
                  <button
                    type="button"
                    className="jt-action"
                    data-severity={action.severity}
                    onClick={() => onGoTo(action.tab, action.sub)}
                  >
                    <span className="jt-action-top">
                      <StatusBadge tone={SEVERITY_TONE[action.severity]}>
                        {SEVERITY_LABEL[action.severity]}
                      </StatusBadge>
                      <span className="jt-action-label">{action.label}</span>
                      {action.owner && <span className="jt-action-owner">{action.owner}</span>}
                    </span>
                    <span className="jt-action-consequence">{action.consequence}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      }
      chart={
        <Card
          title="Journey readiness"
          sub="นับจากสิ่งที่ต้องตั้งค่าล่วงหน้า ไม่ใช่คิวงานตอนนี้"
        >
          {readiness.error ? (
            <Alert tone="danger" title="อ่านความพร้อมไม่สำเร็จ">{readiness.error}</Alert>
          ) : readiness.loading ? (
            <p className="jt-field-hint">กำลังอ่าน…</p>
          ) : (
            <>
              {readiness.readiness?.partial && (
                <Alert tone="warning" title="ตัวเลขนี้เป็นของเท่าที่เห็น">
                  บางเฟสมีข้อที่ Token นี้อ่านไม่ได้ จึงนับไม่ได้ว่าผ่านหรือไม่ผ่าน
                </Alert>
              )}
              <ul className="jt-checks">
                {phases.map((phase) => {
                  const firstFail = phase.checks.find((c) => c.status === 'fail');
                  return (
                    <li key={phase.key} className="jt-check">
                      <div className="jt-check-copy">
                        <p className="jt-check-label">{phase.label}</p>
                        <p className="jt-check-detail">
                          {phase.applicable === 0
                            ? 'ไม่มีข้อที่อ่านได้'
                            : `${phase.passed}/${phase.applicable} ข้อ · ${PHASE_STATE[phase.state].text}`}
                        </p>
                        {firstFail && (
                          <Button size="sm" variant="ghost" onClick={() => onGoTo(firstFail.tab)}>
                            ไปแก้: {firstFail.label}
                          </Button>
                        )}
                      </div>
                      <span className="jt-check-trail">
                        <StatusBadge tone={PHASE_STATE[phase.state].tone}>
                          {phase.percent === null ? '—' : `${phase.percent}%`}
                        </StatusBadge>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </Card>
      }
      list={
        <div className="jt-section">
          <Card title="โซนที่ขายไปแล้ว" sub="เทียบกับความจุที่ตั้งไว้ของแต่ละโซน">
            {!stats ? (
              <p className="jt-field-hint">กำลังโหลด…</p>
            ) : stats.zones.length === 0 ? (
              <FeedbackState state="empty" title="ยังไม่มีโซน">
                โซนจะปรากฏที่นี่เมื่อมีประเภทบัตรที่ตั้งความจุไว้
              </FeedbackState>
            ) : (
              [...stats.zones].sort((a, b) => b.sold - a.sold).map((zone) => (
                <MeterRow
                  key={zone.id}
                  label={zone.nameTh || zone.nameEn}
                  value={`${zone.sold.toLocaleString('th-TH')} / ${zone.capacity.toLocaleString('th-TH')}`}
                  percent={pct(zone.sold, zone.capacity)}
                  tone={zone.remaining === 0 ? 'positive' : undefined}
                  caption={`${fmt(zone.revenue)} · รอชำระ ${(zone.reservedOnline ?? 0).toLocaleString('th-TH')} · พิมพ์รอขาย ${(zone.reservedPrinted ?? 0).toLocaleString('th-TH')} · เหลือโควตา ${zone.remaining.toLocaleString('th-TH')} ใบ`}
                />
              ))
            )}
          </Card>

          {topChannels.length > 0 && (
            <Card
              title="ช่องทางการขาย"
              sub="สี่ช่องทางที่มีคลิกมากที่สุด"
              action={<Button size="sm" variant="ghost" onClick={() => onGoTo('channels')}>ดูทั้งหมด</Button>}
            >
              {topChannels.map((channel) => (
                <MeterRow
                  key={channel.channelId ?? channel.label}
                  label={channel.label}
                  /* Null clicks is "no link to have been clicked", not zero clicks. */
                  value={channel.clicks === null ? '—' : `${channel.clicks.toLocaleString('th-TH')} คลิก`}
                  percent={pct(channel.clicks ?? 0, maxClicks)}
                  caption={`จ่ายแล้ว ${channel.paidOrders.toLocaleString('th-TH')} · ${fmt(channel.revenue)}`}
                />
              ))}
            </Card>
          )}

          <Card
            title="แนวโน้มรายได้"
            action={
              <div className="jt-chips" role="radiogroup" aria-label="ช่วงเวลาของแนวโน้มรายได้">
                {RANGES.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    role="radio"
                    aria-checked={trend.range === opt.key}
                    className="jt-chip"
                    data-active={trend.range === opt.key}
                    tabIndex={trend.range === opt.key ? 0 : -1}
                    onClick={() => trend.setRange(opt.key)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            }
          >
            {trend.error ? (
              <Alert tone="danger" title="โหลดแนวโน้มรายได้ไม่สำเร็จ">{trend.error}</Alert>
            ) : !trend.series ? (
              <p className="jt-field-hint">กำลังโหลด…</p>
            ) : trend.series.length === 0 ? (
              <FeedbackState state="empty" title="ยังไม่มียอดขายในช่วงนี้" />
            ) : (
              <BarChart
                label="รายได้ต่อวัน"
                data={trend.series.map((point) => ({
                  label: point.date.slice(5),
                  value: point.revenue,
                  display: `${fmt(point.revenue)} · ${point.orders} ออร์เดอร์`,
                }))}
                caption={`รวม ${fmt(trend.series.reduce((s, p) => s + p.revenue, 0))} จาก ${trend.series.reduce((s, p) => s + p.orders, 0).toLocaleString('th-TH')} ออร์เดอร์`}
              />
            )}
          </Card>

          <p className="jt-field-hint">
            {withOps
              ? 'ทุกตัวเลขบนหน้านี้อ่านจาก endpoint ที่มีอยู่จริง · ยังไม่มีไทม์ไลน์สดและอัตราการสแกนต่อนาที เพราะยังไม่มีบันทึกเหตุการณ์กลางให้คำนวณ'
              : 'Token ฝ่ายขายเห็นเฉพาะส่วนการขาย · งานที่เลยกำหนด ความเสี่ยง คิว และรอบลงของ เป็นข้อมูลของทีมปฏิบัติการ ซึ่งสิทธิ์ของ Token นี้เข้าไม่ถึง'}
          </p>
        </div>
      }
    />
  );
}

export function CommandCenterDestination({
  token, eventSlug, destination, permitted, role, stats, statsError, channels, onGoTo,
}: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
  stats: AdminStatsDto | null;
  statsError?: string;
  channels: SalesChannelReportDto[] | null;
  onGoTo: (tab: ReadinessTab | 'review', sub?: 'awaiting_verification' | 'problem') => void;
}) {
  const state = useCommandCenter(token, eventSlug, role ?? null);
  const readiness = useEventReadiness(token, eventSlug, role ?? null);
  const trend = useRevenueTrend(token, eventSlug);
  return (
    <CommandCenterScreen
      destination={destination}
      state={state}
      readiness={readiness}
      trend={trend}
      stats={stats}
      statsError={statsError}
      channels={channels}
      permitted={permitted}
      role={role}
      onGoTo={onGoTo}
    />
  );
}
